// Athlete-first enrolment picker for the mobile view (see
// `features/mobile-admin.md`) — the reverse of the attendance tab's
// class-first flow. Lists the company's training classes for one athlete;
// classes they already attend are flagged and tapping any other enrols them
// immediately (the dialog stays open, so one athlete can join several
// classes in a row). Uses the same enrolment endpoint and company-snapshot
// response as the class-first dialog.

import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SchoolIcon from '@mui/icons-material/School';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import type {
  Athlete,
  TrainingClass,
  TrainingClassEnrollment,
} from '../domain';
import type { TrainingClassesSnapshot } from '../data/apiClient';
import { addTrainingClassEnrollment } from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';

interface MobileEnrollDialogProps {
  open: boolean;
  /** The athlete being enrolled. */
  athlete: Athlete;
  /** The company's training classes. */
  classes: TrainingClass[];
  /** Live enrolments across all classes (to flag where the athlete already is). */
  enrollments: TrainingClassEnrollment[];
  onClose: () => void;
  /** Applies the refreshed company snapshot after each enrolment. */
  onSnapshot: (snapshot: TrainingClassesSnapshot) => void;
}

export function MobileEnrollDialog({
  open,
  athlete,
  classes,
  enrollments,
  onClose,
  onSnapshot,
}: MobileEnrollDialogProps) {
  const { t } = useTranslation();

  // The class whose enrolment request is in flight (shows its spinner).
  const [busyClassId, setBusyClassId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enrolledClassIds = new Set(
    enrollments
      .filter((e) => e.athleteId === athlete.personalNumber)
      .map((e) => e.trainingClassId),
  );

  const enroll = async (trainingClass: TrainingClass) => {
    if (busyClassId !== null) return;
    setBusyClassId(trainingClass.id);
    setError(null);
    try {
      const snapshot = await addTrainingClassEnrollment(trainingClass.id, {
        athleteId: athlete.personalNumber,
      });
      onSnapshot(snapshot);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : t('admin.trainingClasses.errors.member'),
      );
    } finally {
      setBusyClassId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busyClassId !== null ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        {t('admin.mobile.roster.enrollTitle', { name: athlete.fullName })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}

          {classes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('admin.mobile.attendance.noClasses')}
            </Typography>
          ) : (
            <List disablePadding>
              {classes.map((trainingClass) => {
                const enrolled = enrolledClassIds.has(trainingClass.id);
                const busy = busyClassId === trainingClass.id;
                return (
                  <ListItemButton
                    key={trainingClass.id}
                    disabled={enrolled || busyClassId !== null}
                    onClick={() => void enroll(trainingClass)}
                    sx={{ borderRadius: 2, px: 1 }}
                  >
                    <ListItemIcon>
                      <SchoolIcon color={enrolled ? 'disabled' : 'primary'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={trainingClass.name}
                      slotProps={{ primary: { noWrap: true } }}
                    />
                    {busy ? (
                      <CircularProgress size={20} />
                    ) : enrolled ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CheckIcon />}
                        label={t('admin.mobile.roster.enrolled')}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', color: 'primary.main' }}>
                        <AddIcon fontSize="small" />
                      </Box>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busyClassId !== null} onClick={onClose}>
          {t('admin.common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
